import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, ApiError, types } from "../api/index.js";
import { apiLink, relativeTime } from "../utils/format.js";
import { navigate, getSearchParam, setSearchParam } from "../router.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";

type SeverityFilter = "all" | "Critical" | "High" | "Medium" | "Low" | "Negligible" | "Unknown";
type ViewMode = "cves" | "images" | "components";

const SEVERITY_ORDER: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
  Negligible: 4,
  Unknown: 5,
};

const SEVERITY_COLORS: Record<string, { dot: string; badge: string }> = {
  Critical: {
    dot: "bg-red-500",
    badge: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-400 dark:border-red-700/50",
  },
  High: {
    dot: "bg-orange-400",
    badge: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-400 dark:border-orange-700/50",
  },
  Medium: {
    dot: "bg-yellow-400",
    badge: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-700/50",
  },
  Low: {
    dot: "bg-sky-400",
    badge: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700/50",
  },
  Negligible: {
    dot: "bg-gray-400",
    badge: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600/50",
  },
  Unknown: {
    dot: "bg-gray-500",
    badge: "bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800/50 dark:text-gray-500 dark:border-gray-700/50",
  },
};

interface ImageRow {
  image: string;
  tags: string[];
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  cves: types.CatalogCveFinding[];
}

interface ComponentRow {
  component: string;
  cveCount: number;
  maxSeverity: string;
  images: string[];
  cves: types.CatalogCveFinding[];
}

@customElement("vulnerabilities-page")
export class VulnerabilitiesPage extends LitElement {
  @state() private _severity: SeverityFilter = "all";
  @state() private _filter = "";
  @state() private _view: ViewMode = (getSearchParam("view") as ViewMode) || "cves";
  @state() private _expanded = new Set<string>();

  createRenderRoot() {
    return this;
  }

  private _setView(view: ViewMode) {
    this._view = view;
    this._expanded = new Set();
    setSearchParam("view", view);
  }

  private _task = new Task(this, {
    task: () => fetchApi<types.CatalogVulnerabilitiesResponse>("/v1/vulnerabilities"),
    args: () => [] as const,
  });

  private _severityBadge(severity: string) {
    const cls = SEVERITY_COLORS[severity]?.badge ??
      "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600/50";
    return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${cls}">${severity}</span>`;
  }

  private static _componentNames(cve: types.CatalogCveFinding): string[] {
    return cve.components.map((c) => c.name);
  }

  private _filtered(data: types.CatalogVulnerabilitiesResponse): types.CatalogCveFinding[] {
    let cves = data.cves;
    if (this._severity !== "all") {
      cves = cves.filter((c) => c.severity === this._severity);
    }
    if (this._filter) {
      const q = this._filter.toLowerCase();
      cves = cves.filter(
        (c) =>
          c.id.toLowerCase().includes(q) ||
          VulnerabilitiesPage._componentNames(c).some((n) => n.toLowerCase().includes(q)) ||
          c.description.toLowerCase().includes(q),
      );
    }
    return cves;
  }

  private _imageRows(data: types.CatalogVulnerabilitiesResponse): ImageRow[] {
    const map = new Map<string, ImageRow>();
    for (const cve of this._filtered(data)) {
      const seenImages = new Set<string>();
      for (const idx of cve.affected) {
        const tag = data.tags[idx];
        if (!tag || seenImages.has(tag.image)) continue;
        seenImages.add(tag.image);
        let row = map.get(tag.image);
        if (!row) {
          row = { image: tag.image, tags: [], total: 0, critical: 0, high: 0, medium: 0, low: 0, cves: [] };
          map.set(tag.image, row);
        }
        row.total++;
        row.cves.push(cve);
        switch (cve.severity) {
          case "Critical": row.critical++; break;
          case "High": row.high++; break;
          case "Medium": row.medium++; break;
          case "Low": row.low++; break;
        }
      }
    }
    for (const [image, row] of map) {
      const tagSet = new Set<string>();
      for (const tag of data.tags) {
        if (tag.image === image) tagSet.add(`${tag.stream} ${tag.variant}`);
      }
      row.tags = [...tagSet].sort();
    }
    return [...map.values()].toSorted(
      (a, b) => b.critical - a.critical || b.total - a.total || a.image.localeCompare(b.image),
    );
  }

  private _componentRows(data: types.CatalogVulnerabilitiesResponse): ComponentRow[] {
    const map = new Map<string, ComponentRow>();
    for (const cve of this._filtered(data)) {
      for (const compName of VulnerabilitiesPage._componentNames(cve)) {
        const baseName = compName.replace(/@[^@]*$/, "");
        let row = map.get(baseName);
        if (!row) {
          row = { component: baseName, cveCount: 0, maxSeverity: "Unknown", images: [], cves: [] };
          map.set(baseName, row);
        }
        if (!row.cves.some((c) => c.id === cve.id)) {
          row.cveCount++;
          row.cves.push(cve);
        }
        if ((SEVERITY_ORDER[cve.severity] ?? 99) < (SEVERITY_ORDER[row.maxSeverity] ?? 99)) {
          row.maxSeverity = cve.severity;
        }
        const images = new Set(row.images);
        for (const idx of cve.affected) {
          const tag = data.tags[idx];
          if (tag) images.add(tag.image);
        }
        row.images = [...images];
      }
    }
    return [...map.values()].toSorted(
      (a, b) =>
        (SEVERITY_ORDER[a.maxSeverity] ?? 99) - (SEVERITY_ORDER[b.maxSeverity] ?? 99) ||
        b.cveCount - a.cveCount ||
        a.component.localeCompare(b.component),
    );
  }

  private _toggleExpand(id: string) {
    const next = new Set(this._expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._expanded = next;
  }

  private _renderSummary(summary: types.CatalogVulnerabilitiesSummary, computedAt: string) {
    return html`
      <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
        <span>${summary.total} unique CVEs</span>
        <span>${summary.images_scanned} images scanned</span>
        <span>${summary.images_with_cves} with CVEs</span>
        <span class="text-gray-500 text-xs">Updated ${relativeTime(computedAt)}</span>
        ${apiLink("/v1/vulnerabilities")}
      </div>
    `;
  }

  private _renderSeverityFilter(summary: types.CatalogVulnerabilitiesSummary) {
    const items: { label: string; count: number; color: string }[] = [
      { label: "Critical", count: summary.critical, color: "bg-red-500" },
      { label: "High", count: summary.high, color: "bg-orange-400" },
      { label: "Medium", count: summary.medium, color: "bg-yellow-400" },
      { label: "Low", count: summary.low, color: "bg-sky-400" },
      { label: "Negligible", count: summary.negligible, color: "bg-gray-400" },
      { label: "Unknown", count: summary.unknown, color: "bg-gray-500" },
    ];
    return html`
      <div class="flex flex-wrap gap-2 mb-4">
        ${items.map(
          ({ label, count, color }) => html`
            <button
              @click=${() => {
                this._severity = this._severity === (label as SeverityFilter) ? "all" : (label as SeverityFilter);
                this._expanded = new Set();
              }}
              class="flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-colors
                     ${this._severity === label ? "ring-1 ring-hb-accent bg-hb-surface" : "hover:bg-hb-surface/50"}"
            >
              <span class="w-2.5 h-2.5 rounded-full ${color}"></span>
              <span class="text-gray-700 dark:text-gray-300">${label}</span>
              <span class="text-gray-500 dark:text-gray-400 font-mono">${count}</span>
            </button>
          `,
        )}
      </div>
    `;
  }

  private _renderTabs() {
    const tabs: Array<{ id: ViewMode; label: string }> = [
      { id: "cves", label: "CVEs" },
      { id: "images", label: "Images" },
      { id: "components", label: "Components" },
    ];
    return html`
      <div class="flex flex-wrap items-center justify-between border-b border-hb-border mb-4 pb-2">
        <div class="flex flex-wrap gap-1">
          ${tabs.map(
            (t) => html`
              <button
                @click=${() => this._setView(t.id)}
                class="px-3 py-1.5 text-sm rounded-t transition-colors cursor-pointer
                       ${this._view === t.id
                         ? "bg-hb-surface text-hb-accent border border-hb-border border-b-hb-surface -mb-[3px]"
                         : "text-gray-400 hover:text-gray-200"}"
              >
                ${t.label}
              </button>
            `,
          )}
        </div>
        <input
          type="text"
          placeholder=${this._view === "cves"
            ? "Filter by CVE ID, component, description..."
            : this._view === "images"
              ? "Filter images by CVE..."
              : "Filter components by CVE..."}
          .value=${this._filter}
          @input=${(e: Event) => {
            this._filter = (e.target as HTMLInputElement).value;
            this._expanded = new Set();
          }}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-xs text-gray-200
                 placeholder:text-gray-500 focus:outline-none focus:border-hb-accent w-64"
        />
      </div>
    `;
  }

  private _renderAffectedTags(cve: types.CatalogCveFinding, tags: types.AffectedTag[]) {
    const grouped = new Map<string, string[]>();
    for (const idx of cve.affected) {
      const tag = tags[idx];
      if (!tag) continue;
      let variants = grouped.get(tag.image);
      if (!variants) {
        variants = [];
        grouped.set(tag.image, variants);
      }
      variants.push(`${tag.stream} ${tag.variant === "default" ? "" : tag.variant}`.trim());
    }
    return html`
      <div class="mt-1 space-y-1">
        ${[...grouped.entries()].map(
          ([image, variants]) => html`
            <div class="flex items-baseline gap-2 min-w-0">
              <a
                href="/image/${image}?tab=vulnerabilities"
                @click=${(e: Event) => { e.preventDefault(); navigate(`/image/${image}?tab=vulnerabilities`); }}
                class="shrink-0 text-hb-accent hover:underline text-xs"
              >${image}</a>
              <span class="text-gray-500 text-xs truncate min-w-0">${variants.join(", ")}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _renderCveTable(data: types.CatalogVulnerabilitiesResponse) {
    const cves = this._filtered(data);
    if (cves.length === 0) return this._emptyState();

    const base = this._severity !== "all"
      ? data.cves.filter((c) => c.severity === this._severity).length
      : data.summary.total;

    return html`
      <div class="text-gray-500 text-xs mb-2">
        ${this._severity !== "all" || this._filter
          ? `Showing ${cves.length} of ${base}`
          : ""}
      </div>
      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">Severity</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">CVE</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">Components</th>
              <th class="py-2 px-3">Description</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Images</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">First Seen</th>
            </tr>
          </thead>
          <tbody>
            ${cves.map((cve) => {
              const imageCount = new Set(cve.affected.map((i) => data.tags[i]?.image).filter(Boolean)).size;
              const expanded = this._expanded.has(cve.id);
              return html`
                <tr
                  class="border-b border-hb-border/50 hover:bg-hb-surface/30 cursor-pointer"
                  tabindex="0"
                  role="button"
                  aria-expanded=${expanded ? "true" : "false"}
                  @click=${() => this._toggleExpand(cve.id)}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this._toggleExpand(cve.id); } }}
                >
                  <td class="py-1.5 px-3 whitespace-nowrap">${this._severityBadge(cve.severity)}</td>
                  <td class="py-1.5 px-3 font-mono whitespace-nowrap">
                    ${cve.url
                      ? html`<a href=${cve.url} target="_blank" rel="noopener" class="text-hb-accent hover:underline" @click=${(e: Event) => e.stopPropagation()}>${cve.id}</a>`
                      : html`<span class="text-gray-200">${cve.id}</span>`}
                  </td>
                  <td class="py-1.5 px-3 text-gray-200 font-mono whitespace-nowrap">${VulnerabilitiesPage._componentNames(cve).join(", ")}</td>
                  <td class="py-1.5 px-3 text-gray-400 truncate max-w-0">${cve.description}</td>
                  <td class="py-1.5 px-3 text-center text-gray-300 whitespace-nowrap">${imageCount}</td>
                  <td class="py-1.5 px-3 text-gray-400 whitespace-nowrap">${cve.first_seen ? relativeTime(cve.first_seen) : "—"}</td>
                </tr>
                ${expanded
                  ? html`
                      <tr class="bg-hb-surface/20">
                        <td colspan="6" class="px-3 py-2">
                          <div class="w-0 min-w-full overflow-hidden">
                            ${this._renderAffectedTags(cve, data.tags)}
                          </div>
                        </td>
                      </tr>
                    `
                  : nothing}
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderImagesTable(data: types.CatalogVulnerabilitiesResponse) {
    const rows = this._imageRows(data);
    if (rows.length === 0) return this._emptyState();

    return html`
      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">Image</th>
              <th class="py-2 px-3">Tags</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Total</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Critical</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">High</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Medium</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Low</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => {
              const expanded = this._expanded.has(row.image);
              return html`
                <tr
                  class="border-b border-hb-border/50 hover:bg-hb-surface/30 cursor-pointer"
                  tabindex="0"
                  role="button"
                  aria-expanded=${expanded ? "true" : "false"}
                  @click=${() => this._toggleExpand(row.image)}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this._toggleExpand(row.image); } }}
                >
                  <td class="py-1.5 px-3 font-bold whitespace-nowrap">
                    <a
                      href="/image/${row.image}?tab=vulnerabilities"
                      @click=${(e: Event) => { e.stopPropagation(); e.preventDefault(); navigate(`/image/${row.image}?tab=vulnerabilities`); }}
                      class="text-hb-accent hover:underline"
                    >${row.image}</a>
                  </td>
                  <td class="py-1.5 px-3 text-gray-500">${row.tags.join(", ")}</td>
                  <td class="py-1.5 px-3 text-center text-gray-200 font-mono whitespace-nowrap">${row.total}</td>
                  <td class="py-1.5 px-3 text-center font-mono whitespace-nowrap ${row.critical > 0 ? "text-red-400" : "text-gray-500"}">${row.critical}</td>
                  <td class="py-1.5 px-3 text-center font-mono whitespace-nowrap ${row.high > 0 ? "text-orange-400" : "text-gray-500"}">${row.high}</td>
                  <td class="py-1.5 px-3 text-center font-mono whitespace-nowrap ${row.medium > 0 ? "text-yellow-400" : "text-gray-500"}">${row.medium}</td>
                  <td class="py-1.5 px-3 text-center font-mono whitespace-nowrap ${row.low > 0 ? "text-sky-400" : "text-gray-500"}">${row.low}</td>
                </tr>
                ${expanded
                  ? html`
                      <tr class="bg-hb-surface/20">
                        <td colspan="7" class="px-3 py-2">
                          <div class="w-0 min-w-full overflow-hidden">
                            <div class="space-y-0.5">
                              ${row.cves.map(
                                (cve) => html`
                                  <div class="flex items-center gap-2 min-w-0">
                                    <span class="shrink-0">${this._severityBadge(cve.severity)}</span>
                                    ${cve.url
                                      ? html`<a href=${cve.url} target="_blank" rel="noopener" class="shrink-0 text-hb-accent hover:underline text-xs font-mono">${cve.id}</a>`
                                      : html`<span class="shrink-0 text-gray-200 text-xs font-mono">${cve.id}</span>`}
                                    <span class="shrink-0 text-gray-400 text-xs font-mono">${VulnerabilitiesPage._componentNames(cve).join(", ")}</span>
                                    <span class="text-gray-500 text-xs truncate min-w-0">${cve.description}</span>
                                  </div>
                                `,
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    `
                  : nothing}
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderComponentsTable(data: types.CatalogVulnerabilitiesResponse) {
    const rows = this._componentRows(data);
    if (rows.length === 0) return this._emptyState();

    return html`
      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3">Component</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">CVEs</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap">Worst</th>
              <th class="py-2 px-3 w-[1%] whitespace-nowrap text-center">Images</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => {
              const expanded = this._expanded.has(row.component);
              return html`
                <tr
                  class="border-b border-hb-border/50 hover:bg-hb-surface/30 cursor-pointer"
                  tabindex="0"
                  role="button"
                  aria-expanded=${expanded ? "true" : "false"}
                  @click=${() => this._toggleExpand(row.component)}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this._toggleExpand(row.component); } }}
                >
                  <td class="py-1.5 px-3 text-gray-200 font-mono">${row.component}</td>
                  <td class="py-1.5 px-3 text-center text-gray-200 font-mono whitespace-nowrap">${row.cveCount}</td>
                  <td class="py-1.5 px-3 whitespace-nowrap">${this._severityBadge(row.maxSeverity)}</td>
                  <td class="py-1.5 px-3 text-center text-gray-300 whitespace-nowrap">${row.images.length}</td>
                </tr>
                ${expanded
                  ? html`
                      <tr class="bg-hb-surface/20">
                        <td colspan="4" class="px-3 py-2">
                          <div class="w-0 min-w-full overflow-hidden">
                            <div class="space-y-0.5 mb-2">
                              ${row.cves.map(
                                (cve) => html`
                                  <div class="flex items-center gap-2 min-w-0">
                                    <span class="shrink-0">${this._severityBadge(cve.severity)}</span>
                                    ${cve.url
                                      ? html`<a href=${cve.url} target="_blank" rel="noopener" class="shrink-0 text-hb-accent hover:underline text-xs font-mono">${cve.id}</a>`
                                      : html`<span class="shrink-0 text-gray-200 text-xs font-mono">${cve.id}</span>`}
                                    <span class="text-gray-500 text-xs truncate min-w-0">${cve.description}</span>
                                  </div>
                                `,
                              )}
                            </div>
                            <div class="flex flex-wrap gap-2">
                              ${row.images.toSorted().map(
                                (img) => html`
                                  <a
                                    href="/image/${img}?tab=vulnerabilities"
                                    @click=${(e: Event) => { e.preventDefault(); navigate(`/image/${img}?tab=vulnerabilities`); }}
                                    class="text-hb-accent hover:underline text-xs"
                                  >${img}</a>
                                `,
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    `
                  : nothing}
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  private _emptyState() {
    return html`<p class="text-gray-500 text-sm py-8 text-center">No results match your filters.</p>`;
  }

  private _renderData(data: types.CatalogVulnerabilitiesResponse) {
    if (data.summary.total === 0) {
      return html`
        ${this._renderSummary(data.summary, data.computed_at)}
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <div class="text-3xl mb-3">✅</div>
          <h3 class="text-gray-300 text-lg font-bold mb-1">No vulnerabilities found</h3>
          <p class="text-gray-500 text-sm">All scanned images are clean.</p>
        </div>
      `;
    }

    return html`
      ${this._renderSummary(data.summary, data.computed_at)}
      ${this._renderTabs()}
      ${this._view === "cves" ? this._renderSeverityFilter(data.summary) : nothing}
      ${this._view === "cves"
        ? this._renderCveTable(data)
        : this._view === "images"
          ? this._renderImagesTable(data)
          : this._renderComponentsTable(data)}
    `;
  }

  render() {
    return html`
      <div>
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 class="text-white text-2xl font-bold">Security Overview</h1>
            <p class="text-gray-400 text-sm mt-1">
              CVE exposure across all catalog images, sorted by priority.
            </p>
          </div>
        </div>

        ${this._task.render({
          pending: () => html`<loading-spinner></loading-spinner>`,
          error: (e) =>
            e instanceof ApiError && e.status === 404
              ? html`<p class="text-gray-500 text-sm py-8 text-center">
                  No vulnerability data available yet. The metrics pipeline has not run.
                </p>`
              : html`<error-message .message=${String(e)}></error-message>`,
          complete: (data) => this._renderData(data),
        })}
      </div>
    `;
  }
}
